import React, { useState, useRef, useEffect } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { publicApi } from '../services/api';
import { Paperclip, X, ArrowUp, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ACCEPT_ATTR = 'application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf';
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic)$/i;

/**
 * Minimalist "consultation note" layout: no bubbles, no avatars, no cards.
 * Messages are rendered as typographical entries on a vertical stack, separated
 * by subtle hairlines. The doctor is identified by the client's accent color on
 * the name; the patient is rendered in neutral gray. Own messages are dimmed
 * only slightly so conversation reads top-to-bottom like a clinical log.
 *
 * Deliberately avoids the Messenger/WhatsApp bubble idiom so it feels unlike
 * the customer's other telemedicine platform.
 */
export default function Chat({ roomId, userName, userRole, isOpen, onToggle, accentColor, chatToken, myIdentity, onRemoteMessage }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const connectionRef = useRef(null);
  const myIdentityRef = useRef(myIdentity);
  myIdentityRef.current = myIdentity;
  const onRemoteMessageRef = useRef(onRemoteMessage);
  onRemoteMessageRef.current = onRemoteMessage;

  const accent = accentColor || '#10b981'; // emerald-500 for doctor
  const patientColor = '#f59e0b'; // amber-500 for patient - strong contrast

  useEffect(() => {
    if (!roomId || !chatToken) return;

    (async () => {
      try {
        const msgs = await publicApi.getMessages(roomId, chatToken);
        if (Array.isArray(msgs)) setMessages(msgs);
      } catch {}
    })();

    (async () => {
      try {
        const conn = new HubConnectionBuilder()
          .withUrl(`${window.location.origin}/videohub`, {
            accessTokenFactory: () => chatToken,
          })
          .configureLogging(LogLevel.Warning)
          .withAutomaticReconnect()
          .build();

        conn.on('NewMessage', (msg) => {
          // Ignore own broadcast echo - the sender already added the message
          // optimistically below. Compare by identity (JWT sub) instead of name,
          // which could collide across tenants.
          if (msg.senderIdentity && myIdentityRef.current && msg.senderIdentity === myIdentityRef.current) {
            return;
          }
          setMessages(prev => [...prev, msg]);
          // Let the parent (EmbedPage) know so it can bump the unread counter
          // on the Controls chat button while the panel is hidden.
          onRemoteMessageRef.current?.(msg);
        });

        await conn.start();
        await conn.invoke('JoinRoom', roomId);
        connectionRef.current = conn;
      } catch (err) {
        console.error('SignalR error:', err);
      }
    })();

    return () => {
      connectionRef.current?.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [roomId, chatToken]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await publicApi.sendMessage(roomId, chatToken, userName, userRole, text);
      setMessages(prev => [...prev, {
        senderName: userName,
        senderRole: userRole,
        senderIdentity: myIdentity,
        content: text,
        type: 'text',
        sentAt: new Date().toISOString(),
      }]);
      setInput('');
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  const handlePickFile = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`${file.name}: ${formatBytes(file.size)} excede o limite de 10 MB.`);
      return;
    }

    setUploadError('');
    setUploading(true);
    try {
      const uploaded = await publicApi.uploadAttachment(roomId, chatToken, file, userName, userRole);
      if (uploaded?.error) {
        setUploadError(uploaded.error);
        return;
      }
      const isImage = uploaded.kind === 'image' || IMAGE_EXT.test(uploaded.fileName || '');
      const msgType = isImage ? 'image' : 'file';
      await publicApi.sendMessage(
        roomId, chatToken, userName, userRole,
        uploaded.fileName, msgType,
        { fileUrl: uploaded.fileUrl, fileName: uploaded.fileName },
      );
      setMessages(prev => [...prev, {
        senderName: userName,
        senderRole: userRole,
        senderIdentity: myIdentity,
        content: uploaded.fileName,
        type: msgType,
        fileUrl: uploaded.fileUrl,
        fileName: uploaded.fileName,
        sentAt: new Date().toISOString(),
      }]);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError('Falha ao enviar o arquivo. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  // Render a hidden, absolutely-positioned placeholder when closed so the SignalR
  // subscription above stays alive and unread counts keep ticking. The parent
  // collapses the wrapper to width 0, this element is not visible either way.
  if (!isOpen) return <span aria-hidden className="hidden" />;

  return (
    <div className="flex flex-col h-full w-80 bg-[#111418] text-slate-200" style={{ fontFamily: 'ui-sans-serif, system-ui' }}>
      {/* Header: subtle, no counter, no decoration */}
      <div className="flex items-center justify-between px-4 h-11 border-b border-white/5">
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
          Conversa
        </span>
        <button
          onClick={onToggle}
          className="text-slate-500 hover:text-slate-200 -mr-1 p-1"
          aria-label="Fechar conversa"
        >
          <X size={15} />
        </button>
      </div>

      {/* Document-style message log */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <p className="text-[12px] text-slate-500 leading-relaxed">
            Nenhuma mensagem trocada ainda.
          </p>
        ) : (
          <ol className="space-y-3">
            {messages.map((msg, i) => (
              <MessageEntry
                key={i}
                msg={msg}
                doctorColor={accent}
                patientColor={patientColor}
                chatToken={chatToken}
              />
            ))}
          </ol>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer: single line with a divider above, no card */}
      <div className="border-t border-white/5 px-3 py-2.5">
        {uploadError && (
          <p className="text-[11px] text-red-400 mb-2 px-1">{uploadError}</p>
        )}
        <div className="flex items-end gap-1">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={handlePickFile}
            disabled={uploading || sending}
            className="p-1.5 text-slate-500 hover:text-slate-200 disabled:opacity-40"
            title="Anexar arquivo (PDF, imagem, ate 10 MB)"
            aria-label="Anexar arquivo"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Digite aqui..."
            rows={1}
            className="flex-1 bg-transparent text-[13px] text-slate-100 px-1.5 py-1 outline-none resize-none placeholder-slate-600 max-h-28"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="p-1.5 disabled:opacity-30 transition-colors"
            style={{ color: input.trim() ? accent : undefined }}
            aria-label="Enviar"
          >
            <ArrowUp size={15} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageEntry({ msg, doctorColor, patientColor, chatToken }) {
  const isDoctor = msg.senderRole === 'host';
  const senderColor = isDoctor ? doctorColor : patientColor;
  const roleLabel = isDoctor ? 'MEDICO' : 'PACIENTE';
  const time = new Date(msg.sentAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <li className="relative pl-3">
      {/* Left color bar: role identity at a glance. Always visible so the
          doctor knows instantly who sent what without reading names. */}
      <div
        className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full"
        style={{ backgroundColor: senderColor }}
        aria-hidden
      />
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-[12.5px] font-semibold" style={{ color: senderColor }}>
          {msg.senderName}
        </span>
        <span
          className="text-[9px] font-bold tracking-wider uppercase px-1 py-[1px] rounded"
          style={{ backgroundColor: `${senderColor}22`, color: senderColor }}
        >
          {roleLabel}
        </span>
        <span className="ml-auto text-[10px] text-slate-500 font-mono">
          {time}
        </span>
      </div>
      <MessageBody msg={msg} accent={senderColor} chatToken={chatToken} />
    </li>
  );
}

function MessageBody({ msg, accent, chatToken }) {
  const isImage = msg.type === 'image' || (msg.fileUrl && msg.fileName && IMAGE_EXT.test(msg.fileName));
  const isFile = msg.type === 'file' && msg.fileUrl;

  // Anexa o chatToken como query string quando o fileUrl aponta pra o endpoint
  // novo /api/rooms/.../attachments/. Sem isso, o <a> tag nao manda header
  // Authorization e o download cai em 403. URLs legadas (/chat-uploads/...)
  // passam direto - elas nao esperam token.
  const authedUrl = (msg.fileUrl && msg.fileUrl.includes('/chat/attachments/') && chatToken)
    ? `${msg.fileUrl}${msg.fileUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(chatToken)}`
    : msg.fileUrl;

  if (isImage && msg.fileUrl) {
    return (
      <a href={authedUrl} target="_blank" rel="noreferrer" className="inline-block mt-0.5">
        <img
          src={authedUrl}
          alt={msg.fileName}
          className="max-h-48 rounded opacity-95 hover:opacity-100 transition-opacity"
        />
      </a>
    );
  }

  if (isFile) {
    const isPdf = msg.fileName?.toLowerCase().endsWith('.pdf');
    return (
      <a
        href={authedUrl}
        target="_blank"
        rel="noreferrer"
        download={msg.fileName}
        className="inline-flex items-center gap-1.5 text-[12px] underline decoration-dotted underline-offset-2 hover:no-underline"
        style={{ color: accent }}
      >
        {isPdf ? <FileText size={12} /> : <ImageIcon size={12} />}
        {msg.fileName}
      </a>
    );
  }

  return (
    <p className="text-[13px] text-slate-200 leading-[1.45] whitespace-pre-wrap break-words">
      {linkify(msg.content)}
    </p>
  );
}

const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"])/gi;

function linkify(text) {
  if (!text) return text;
  const parts = [];
  let lastIndex = 0;
  let match;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const raw = match[0];
    const href = raw.startsWith('http') ? raw : `https://${raw}`;
    parts.push(
      <a
        key={`${match.index}-${raw}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-sky-400 hover:text-sky-300 underline decoration-dotted underline-offset-2 hover:no-underline break-all"
      >
        {raw}
      </a>
    );
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : text;
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
